import { useMemo } from 'react'

import { useAPIGetPraktikKinerjaPerencanaanSummary } from 'api/kinerja/pengawas-sekolah/pelaksanaan'
import { usePSStatus } from './useSummary'
import {
  useAPIGetPraktikKinerjaFormValues,
  useAPIPostPraktikKinerjaFormValues,
} from 'api/kinerja/pengawas-sekolah/pelaksanaan/form'
import { getPelaksanaanPraktikKinerjaPageState } from 'app/KinerjaPSPegawaiPage/utils/praktikKinerja'

import type { QueryStatus } from 'react-query'
import type {
  TPenilaianPraktikKinerjaFormValues,
  TPraktikKinerjaFormName,
  TPraktikKinerjaPerencanaanSummary,
  SKPStatus,
  TSubmissionResponse,
} from 'types/kinerja-ps-pegawai'

export type TUsePelaksanaanPraktikKinerjaPS<T, U> = {
  psSummaryHookResult: {
    summary: SKPStatus
    summaryFetchStatus: QueryStatus
    isPelaksanaanState: boolean
  }

  skpPsId: string

  formData: TPenilaianPraktikKinerjaFormValues
  formStatus: QueryStatus

  perencanaanSummary: TPraktikKinerjaPerencanaanSummary
  perencanaanSummaryStatus: QueryStatus

  parsedFormValues: T
  parsedRating: U

  submitFormValues: ({
    values,
    saveAsDraft,
  }: {
    values: T
    saveAsDraft: boolean
  }) => Promise<TSubmissionResponse>
  submitStatus: QueryStatus

  isLoading: boolean
  isSuccess: boolean

  isFetchingFormValues: boolean
  isSuccessfullyFetchedFormValues: boolean
  isFailedToFetchFormValues: boolean
  dataFormValues: TPenilaianPraktikKinerjaFormValues

  isPostingFormValues: boolean
  isSuccessfullyPostedFormValues: boolean
  isFailedToPostFormValues: boolean

  isViewOnly: boolean
  isLocked: boolean
  isNeedRollbackApproval: boolean
  isAppeal: boolean
}

export function usePelaksanaanPraktikKinerjaPS<T = any, U = any>({
  formName,
  skpPsId: providedSkpPsId,
}: {
  formName: TPraktikKinerjaFormName
  skpPsId?: string
}): TUsePelaksanaanPraktikKinerjaPS<T, U> {
  const psSummaryHookResult = usePSStatus({
    disabled: !!providedSkpPsId,
  })

  const { summary: psSummary, summaryFetchStatus: psSummaryFetchStatus } =
    psSummaryHookResult

  const skpPsId = providedSkpPsId ?? psSummary?.skpPsId ?? ''

  const enabledAPI = psSummaryFetchStatus === 'success' || !!skpPsId

  const { data: perencanaanSummary, status: perencanaanSummaryStatus } =
    useAPIGetPraktikKinerjaPerencanaanSummary({
      skpPsId,
      opts: { enabled: enabledAPI },
    })

  const {
    data: formData,
    status: formStatus,
    isFetching: isFetchingFormValues,
    isSuccess: isSuccessfullyFetchedFormValues,
    isError: isFailedToFetchFormValues,
  } = useAPIGetPraktikKinerjaFormValues({
    skpPsId,
    formName,
    opts: {
      enabled: enabledAPI,
    },
  })

  const parsedFormValues = useMemo<T | null>(() => {
    try {
      if (formStatus !== 'success') return null
      return JSON.parse(formData?.formValues ?? '{}') as T
    } catch (e) {
      console.error(e)
      return null
    }
  }, [formStatus, formData?.formValues])

  const parsedRating = useMemo<U | null>(() => {
    try {
      if (formStatus !== 'success') return null
      return JSON.parse(formData?.rating ?? '{}') as U
    } catch (e) {
      console.error(e)
      return null
    }
  }, [formStatus, formData?.rating])

  const {
    mutateAsync,
    status: submitStatus,
    isLoading: isPostingFormValues,
    isSuccess: isSuccessfullyPostedFormValues,
    isError: isFailedToPostFormValues,
  } = useAPIPostPraktikKinerjaFormValues({
    skpPsId,
    formName,
  })

  const { isViewOnly, isLocked, isNeedRollbackApproval, isAppeal } =
    getPelaksanaanPraktikKinerjaPageState(
      psSummary?.pelaksanaanStatuses?.praktikKinerja,
      formName,
      psSummary?.pelaksanaanStatuses?.praktikKinerjaPerubahanStatus
    )

  const submitFormValues = ({
    values,
    saveAsDraft,
  }: {
    values: T
    saveAsDraft: boolean
  }) => mutateAsync({ formValues: JSON.stringify(values), saveAsDraft })

  const isLoading =
    (!providedSkpPsId && psSummaryFetchStatus === 'loading') ||
    formStatus === 'loading' ||
    perencanaanSummaryStatus === 'loading'

  const isSuccess =
    (!!providedSkpPsId || psSummaryFetchStatus === 'success') &&
    formStatus === 'success' &&
    perencanaanSummaryStatus === 'success'

  return {
    psSummaryHookResult,

    skpPsId,

    formData,
    formStatus,

    perencanaanSummary,
    perencanaanSummaryStatus,

    parsedFormValues,
    parsedRating,

    submitFormValues,
    submitStatus,

    isLoading,
    isSuccess,

    isFetchingFormValues,
    isSuccessfullyFetchedFormValues,
    isFailedToFetchFormValues,
    dataFormValues: { ...formData }, // alias to formData

    isPostingFormValues,
    isSuccessfullyPostedFormValues,
    isFailedToPostFormValues,

    // Page state
    isViewOnly,
    isLocked,
    isNeedRollbackApproval,
    isAppeal,
  }
}
